import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import FavoriteBorderOutlinedIcon from "@mui/icons-material/FavoriteBorderOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { useGetAllDesignerAccountsQuery } from "../../../hooks/useGetAllDesignerAccountsQuery";
import { useGetAllHomeownersQuery } from "../../../hooks/useGetAllHomeownersQuery";
import { useGetAllAdminUsersQuery } from "../../../hooks/useGetAllAdminUsersQuery";
import { useGetDesignerApplicationListQuery } from "../../../hooks/useGetDesignerApplicationListQuery";


interface DashboardStats {
  activeUsers: {
    total: number;
    designers: number;
    homeowners: number;
    trend: number;
  };
  matches: {
    total: number;
    averageScore: number;
    trend: number;
    last7Days: number;
  };
  applications: {
    newApplications: number;
    averageScore: number;
    trend: number;
    pipeline: Array<{ label: string; value: number }>; // e.g. Under Review, Approved ...
  };
  matchActivity: {
    last7Days: number;
    last30Days: number;
    loves: number;
    likes: number;
    passes: number;
  };
}

const mockStats: DashboardStats = {
  activeUsers: {
    total: 1234,
    designers: 456,
    homeowners: 778,
    trend: 12.5,
  },
  matches: {
    total: 3456,
    averageScore: 85.3,
    trend: 8.2,
    last7Days: 234,
  },
  applications: {
    newApplications: 23,
    averageScore: 85.3,
    trend: 8.2,
    pipeline: [
      { label: "Under Review", value: 234 },
      { label: "Application In Progress", value: 128 },
      { label: "Account Created", value: 42 },
      { label: "Approved", value: 156 },
      { label: "Rejected", value: 19 },
    ],
  },
  matchActivity: {
    last7Days: 234,
    last30Days: 987,
    loves: 567,
    likes: 567,
    passes: 1234,
  },
};

const formatNumber = (value: number | undefined) =>
  typeof value === "number" ? value.toLocaleString() : "--";

const Trend = ({ value }: { value: number }) => {
  const positive = value >= 0;
  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      {positive ? (
        <TrendingUpIcon color="success" fontSize="small" />
      ) : (
        <TrendingDownIcon color="error" fontSize="small" />
      )}
      <Typography
        variant="body2"
        color={positive ? "success.main" : "error.main"}
        fontWeight={400}
      >
        {Math.abs(value).toFixed(1)}% from last period
      </Typography>
    </Stack>
  );
};

const StatCard = ({
  title,
  value,
  subtitle,
  trend,
  icon,
  accentColor,
  animationDelay = 0,
}: {
  title: string;
  value: string;
  subtitle: string;
  trend: number;
  icon: React.ReactNode;
  accentColor: string;
  animationDelay?: number;
}) => {
  const theme = useTheme();
  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 3, // 12px
        height: "100%",
        backgroundColor: "#fff",
        boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.06)", // Subtle, soft shadow
        animation: 'fadeInUp 0.6s ease-out',
        animationDelay: `${animationDelay}s`,
        animationFillMode: 'both',
        '@keyframes fadeInUp': {
          '0%': {
            opacity: 0,
            transform: 'translateY(30px)'
          },
          '100%': {
            opacity: 1,
            transform: 'translateY(0)'
          }
        }
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Stack spacing={1}>
          <Typography 
            variant="subtitle2" 
            color="text.secondary"
            sx={{
              animation: 'fadeIn 0.4s ease-out',
              animationDelay: `${animationDelay + 0.2}s`,
              animationFillMode: 'both',
              '@keyframes fadeIn': {
                '0%': { opacity: 0 },
                '100%': { opacity: 1 }
              }
            }}
          >
            {title}
          </Typography>
          <Typography 
            variant="h3" 
            fontWeight={700} 
            color="#0DAB76"
            sx={{
              animation: 'scaleIn 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
              animationDelay: `${animationDelay + 0.3}s`,
              animationFillMode: 'both',
              '@keyframes scaleIn': {
                '0%': {
                  opacity: 0,
                  transform: 'scale(0.5)'
                },
                '100%': {
                  opacity: 1,
                  transform: 'scale(1)'
                }
              }
            }}
          >
            {value}
          </Typography>
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{
              animation: 'fadeIn 0.4s ease-out',
              animationDelay: `${animationDelay + 0.4}s`,
              animationFillMode: 'both'
            }}
          >
            {subtitle}
          </Typography>
          <Box
            sx={{
              animation: 'slideInLeft 0.5s ease-out',
              animationDelay: `${animationDelay + 0.5}s`,
              animationFillMode: 'both',
              '@keyframes slideInLeft': {
                '0%': {
                  opacity: 0,
                  transform: 'translateX(-20px)'
                },
                '100%': {
                  opacity: 1,
                  transform: 'translateX(0)'
                }
              }
            }}
          >
          <Trend value={trend} />
          </Box>
        </Stack>
        <Avatar
          sx={{
            backgroundColor: `${accentColor}1a`,
            color: accentColor,
            width: 48,
            height: 48,
            animation: 'rotateIn 0.6s ease-out',
            animationDelay: `${animationDelay + 0.2}s`,
            animationFillMode: 'both',
            '@keyframes rotateIn': {
              '0%': {
                opacity: 0,
                transform: 'rotate(-180deg) scale(0)'
              },
              '100%': {
                opacity: 1,
                transform: 'rotate(0) scale(1)'
              }
            }
          }}
        >
          {icon}
        </Avatar>
      </Stack>
    </Paper>
  );
};

export const DashboardHome: React.FC = () => {
  
  const theme = useTheme();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  // GraphQL hooks for real data
  const { getAllDesignerAccounts, loading: designersLoading } = useGetAllDesignerAccountsQuery();
  const { homeowners, loading: homeownersLoading } = useGetAllHomeownersQuery();
  const { getAllAdminUsers, loading: adminLoading } = useGetAllAdminUsersQuery();
  const { getDesignerApplicationList, loading: applicationsLoading } = useGetDesignerApplicationListQuery();
  
  // Use ref to track if we've loaded stats to prevent infinite loops
  const hasLoadedStats = useRef(false);
  const isLoadingStats = useRef(false);

  const loadStats = useCallback(async () => {
    // Prevent concurrent calls
    if (isLoadingStats.current) {
      return;
    }
    
    isLoadingStats.current = true;
    try {
      
      // Fetch real user data - homeowners is already loaded by useQuery hook
      const [designersData, adminUsersData, applicationsData] = await Promise.all([
        getAllDesignerAccounts(true).catch(err => {
          return [];
        }),
        getAllAdminUsers(true).catch(err => {
          return [];
        }),
        getDesignerApplicationList(null, true).catch(err => {
          return [];
        })
      ]);
      
      // Use homeowners from the query (it's already loaded by useQuery)
      // Access it directly from the hook result to avoid stale closure issues
      const homeownersData = homeowners || [];
      

      // Extract timestamp from MongoDB ObjectId
      const extractTimestampFromObjectId = (objectId: string): Date => {
        // MongoDB ObjectId contains timestamp in first 4 bytes (8 hex chars)
        const timestampHex = objectId.substring(0, 8);
        const timestamp = parseInt(timestampHex, 16) * 1000; // Convert to milliseconds
        return new Date(timestamp);
      };

      // Calculate trend based on designers and homeowners (using ObjectId timestamps)
      // This gives us real user growth trends, not just admin user trends
      const calculateTrend = (): number => {
        // Get designer application IDs (these contain creation timestamps)
        const designerApplicationIds = designersData.map((designer: any) => designer._id);
        
        // Get homeowner IDs (these also contain creation timestamps)
        const homeownerIds = homeownersData.map((homeowner: any) => homeowner._id);
        
        // Combine all user IDs and extract timestamps
        const allUserIds = [...designerApplicationIds, ...homeownerIds];
        
        if (allUserIds.length === 0) return 0;
        
        // Parse creation dates from ObjectIds
        const creationDates = allUserIds.map((id: string) => 
          extractTimestampFromObjectId(id)
        ).sort((a: Date, b: Date) => a.getTime() - b.getTime());
        
        if (creationDates.length < 2) return 0;
        
        // Calculate trend based on recent vs older registrations
        const now = new Date();
        const thirtyDaysAgo = new Date(now.getTime() - (30 * 24 * 60 * 60 * 1000));
        const sixtyDaysAgo = new Date(now.getTime() - (60 * 24 * 60 * 60 * 1000));
        
        const recentRegistrations = creationDates.filter((date: Date) => date >= thirtyDaysAgo).length;
        const previousPeriodRegistrations = creationDates.filter((date: Date) => 
          date >= sixtyDaysAgo && date < thirtyDaysAgo
        ).length;
        
        
        if (previousPeriodRegistrations === 0) {
          return recentRegistrations > 0 ? 100 : 0; // 100% growth if no previous data
        }
        
        const trend = ((recentRegistrations - previousPeriodRegistrations) / previousPeriodRegistrations) * 100;
        return Math.round(trend * 10) / 10; // Round to 1 decimal place
      };

      // Calculate application statistics
      const calculateApplicationStats = () => {
        if (applicationsData.length === 0) {
          return {
            newApplications: 0,
            averageScore: 0,
            trend: 0,
            pipeline: [
              { label: "Under Review", value: 0 },
              { label: "Application In Progress", value: 0 },
              { label: "Account Created", value: 0 },
              { label: "Approved", value: 0 },
              { label: "Rejected", value: 0 },
            ]
          };
        }

        // Calculate application scores based on completeness and status
        const calculateApplicationScore = (app: any): number => {
          let score = 0;
          
          // Base score for having an application
          score += 20;
          
          // Score for experience data
          if (app.experience && app.experience.length > 0) score += 20;
          
          // Score for education data
          if (app.education && app.education.length > 0) score += 15;
          
          // Score for references
          if (app.references && app.references.length > 0) score += 15;
          
          // Score for business goals
          if (app.businessGoals) score += 15;
          
          // Bonus for approved status
          if (app.status === 'Approved') score += 15;
          
          return Math.min(score, 100); // Cap at 100
        };

        // Calculate daily application trends
        const applicationIds = applicationsData.map((app: any) => app._id);
        const applicationDates = applicationIds.map((id: string) => 
          extractTimestampFromObjectId(id)
        ).sort((a: Date, b: Date) => a.getTime() - b.getTime());

        const now = new Date();
        const sevenDaysAgo = new Date(now.getTime() - (7 * 24 * 60 * 60 * 1000));
        const fourteenDaysAgo = new Date(now.getTime() - (14 * 24 * 60 * 60 * 1000));

        const recentApplications = applicationDates.filter((date: Date) => date >= sevenDaysAgo).length;
        const previousPeriodApplications = applicationDates.filter((date: Date) => 
          date >= fourteenDaysAgo && date < sevenDaysAgo
        ).length;

        let applicationTrend = 0;
        if (previousPeriodApplications > 0) {
          applicationTrend = ((recentApplications - previousPeriodApplications) / previousPeriodApplications) * 100;
        } else if (recentApplications > 0) {
          applicationTrend = 100; // 100% growth if no previous data
        }

        // Calculate pipeline statistics
        const statusCounts = applicationsData.reduce((acc: any, app: any) => {
          const status = app.status || 'Incomplete';
          acc[status] = (acc[status] || 0) + 1;
          return acc;
        }, {});

        const pipeline = [
          { label: "Account Created", value: statusCounts['ToBeCompleted'] || 0 },
          { label: "Application In Progress", value: statusCounts['CompleteApplication'] || 0 },
          { label: "Under Review", value: statusCounts['UnderReview'] || 0 },
          { label: "Approved", value: statusCounts['Approved'] || 0 },
          { label: "Membership Active", value: statusCounts['MembershipActive'] || 0 },
          { label: "Rejected", value: statusCounts['Rejected'] || 0 },
        ];

        // Calculate average score
        const scores = applicationsData.map((app: any) => calculateApplicationScore(app));
        const averageScore = scores.length > 0 ? scores.reduce((sum: number, score: number) => sum + score, 0) / scores.length : 0;


        return {
          newApplications: applicationsData.length,
          averageScore: Math.round(averageScore * 10) / 10,
          trend: Math.round(applicationTrend * 10) / 10,
          pipeline
        };
      };

      // Calculate real statistics
      const realStats: DashboardStats = {
        activeUsers: {
          total: designersData.length + homeownersData.length + adminUsersData.length,
          designers: designersData.length,
          homeowners: homeownersData.length,
          trend: calculateTrend(),
        },
        // Keep mock data for matches section for now
        matches: {
          total: 3456,
          averageScore: 85.3,
          trend: 8.2,
          last7Days: 234,
        },
        // Use real application data
        applications: calculateApplicationStats(),
        matchActivity: {
          last7Days: 234,
          last30Days: 987,
          loves: 567,
          likes: 567,
          passes: 1234,
        },
      };

      setStats(realStats);
      setLastUpdated(new Date());
    } catch (error) {
      // Fall back to mock data on error
      setStats(mockStats);
      setLastUpdated(new Date());
    } finally {
      isLoadingStats.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getAllDesignerAccounts, getAllAdminUsers, getDesignerApplicationList]);

  // Load stats once on mount and when all data has finished loading
  useEffect(() => {
    // Only load once when all queries have finished loading and we haven't loaded yet
    if (!hasLoadedStats.current && !isLoadingStats.current && !designersLoading && !homeownersLoading && !adminLoading && !applicationsLoading) {
      hasLoadedStats.current = true;
      loadStats();
    }
  }, [designersLoading, homeownersLoading, adminLoading, applicationsLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  const appliedStats = useMemo(() => stats ?? mockStats, [stats]);
  const loading = designersLoading || homeownersLoading || adminLoading || applicationsLoading;

  if (loading && !stats) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress sx={{ color: '#00A06D' }} />
      </Box>
    );
  }

  return (
    <Stack spacing={3}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, md: 4 },
          borderRadius: 4,
          border: `1px solid ${theme.palette.grey[200]}`,
          backgroundColor: "#fcfdff",
        }}
      >
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={2}>
          <Stack spacing={1}>
            <Typography variant="h4" fontWeight={700}>
              Admin Dashboard
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage users, monitor platform activity, and oversee operations
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="caption" color="text.secondary">
              Last updated: {lastUpdated ? lastUpdated.toLocaleTimeString() : "--"}
            </Typography>
            <IconButton color="primary" onClick={loadStats} aria-label="refresh dashboard">
              <RefreshIcon />
            </IconButton>
          </Stack>
        </Stack>

        <Grid container spacing={3} sx={{ mt: 2 }}>
          <Grid item xs={12} md={4}>
            <StatCard
              title="Active Users"
              value={formatNumber(appliedStats.activeUsers.total)}
              subtitle={`${formatNumber(appliedStats.activeUsers.designers)} designers, ${formatNumber(
                appliedStats.activeUsers.homeowners
              )} homeowners`}
              trend={appliedStats.activeUsers.trend}
              icon={<PeopleAltOutlinedIcon />}
              accentColor={theme.palette.success.main}
              animationDelay={0}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <StatCard
              title="Total Matches"
              value={formatNumber(appliedStats.matches.total)}
              subtitle={`Avg score: ${appliedStats.matches.averageScore.toFixed(1)}%`}
              trend={appliedStats.matches.trend}
              icon={<FavoriteBorderOutlinedIcon />}
              accentColor={theme.palette.primary.main}
              animationDelay={0.15}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <StatCard
              title="New Applications"
              value={formatNumber(appliedStats.applications.newApplications)}
              subtitle={`Avg score: ${appliedStats.applications.averageScore.toFixed(1)}%`}
              trend={appliedStats.applications.trend}
              icon={<CalendarMonthOutlinedIcon />}
              accentColor={theme.palette.secondary.main}
              animationDelay={0.3}
            />
          </Grid>
        </Grid>
        <Grid container spacing={3} sx={{ mt: { xs: 3, md: 4 } }}>
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 4,
                border: `1px solid ${theme.palette.grey[200]}`,
                height: "100%",
              }}
            >
              <Stack spacing={2}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="h6" fontWeight={600}>
                    Match Activity
                  </Typography>
                  <Button
                    size="small"
                    endIcon={<ArrowForwardIosIcon fontSize="small" />}
                    sx={{ textTransform: "none" }}
                  >
                    View report
                  </Button>
                </Stack>
                <Stack spacing={1.5}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography color="text.secondary">Last 7 days</Typography>
                    <Typography fontWeight={600}>{formatNumber(appliedStats.matchActivity.last7Days)}</Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography color="text.secondary">Last 30 days</Typography>
                    <Typography fontWeight={600}>{formatNumber(appliedStats.matchActivity.last30Days)}</Typography>
                  </Stack>
                </Stack>
                <Divider sx={{ my: 1 }} />
                <Stack spacing={1}>
                  <Typography variant="subtitle2" color="text.secondary">
                    User Engagement
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Button size="small" sx={{
                      textTransform: "none",
                      borderRadius: 999,
                      backgroundColor: "#fde2e1",
                      color: "#c62828",
                    }}>
                      Loves: {formatNumber(appliedStats.matchActivity.loves)}
                    </Button>
                    <Button size="small" sx={{
                      textTransform: "none",
                      borderRadius: 999,
                      backgroundColor: "#e8f1ff",
                      color: theme.palette.primary.main,
                    }}>
                      Likes: {formatNumber(appliedStats.matchActivity.likes)}
                    </Button>
                    <Button size="small" sx={{
                      textTransform: "none",
                      borderRadius: 999,
                      backgroundColor: "#fff4d5",
                      color: "#e65100",
                    }}>
                      Not for me: {formatNumber(appliedStats.matchActivity.passes)}
                    </Button>
                  </Stack>
                </Stack>
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 4,
                border: `1px solid ${theme.palette.grey[200]}`,
                height: "100%",
              }}
            >
              <Stack spacing={2}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h6" fontWeight={600}>
                  Application Pipeline
                </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {appliedStats.applications.pipeline.reduce((sum, stage) => sum + stage.value, 0)} total
                  </Typography>
                </Stack>
                <Stack spacing={1.25}>
                  {appliedStats.applications.pipeline.map((stage, index) => {
                    const totalApplications = appliedStats.applications.pipeline.reduce((sum, s) => sum + s.value, 0);
                    const percentage = totalApplications > 0 ? (stage.value / totalApplications) * 100 : 0;
                    const isActive = stage.value > 0;
                    
                    return (
                      <Stack 
                        key={stage.label} 
                        spacing={1}
                        sx={{
                          animation: 'fadeInSlide 0.5s ease-out',
                          animationDelay: `${index * 0.1}s`,
                          animationFillMode: 'both',
                          '@keyframes fadeInSlide': {
                            '0%': {
                              opacity: 0,
                              transform: 'translateX(-20px)'
                            },
                            '100%': {
                              opacity: 1,
                              transform: 'translateX(0)'
                            }
                          }
                        }}
                      >
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                backgroundColor: isActive ? '#0DAB76' : theme.palette.grey[300],
                                opacity: isActive ? 1 : 0.5,
                                animation: isActive ? 'pulse 2s ease-in-out infinite' : 'none',
                                '@keyframes pulse': {
                                  '0%, 100%': {
                                    transform: 'scale(1)',
                                    opacity: 1
                                  },
                                  '50%': {
                                    transform: 'scale(1.2)',
                                    opacity: 0.8
                                  }
                                }
                              }}
                            />
                            <Typography 
                              color={isActive ? "text.primary" : "text.secondary"}
                              fontWeight={isActive ? 500 : 400}
                            >
                              {stage.label}
                            </Typography>
                          </Stack>
                          <Typography 
                            fontWeight={600} 
                            color={isActive ? '#0DAB76' : 'text.secondary'}
                            sx={{
                              animation: isActive ? 'countUp 0.8s ease-out' : 'none',
                              animationDelay: `${index * 0.1 + 0.3}s`,
                              animationFillMode: 'both',
                              '@keyframes countUp': {
                                '0%': {
                                  opacity: 0,
                                  transform: 'scale(0.5)'
                                },
                                '50%': {
                                  transform: 'scale(1.1)'
                                },
                                '100%': {
                                  opacity: 1,
                                  transform: 'scale(1)'
                                }
                              }
                            }}
                          >
                            {formatNumber(stage.value)}
                          </Typography>
                        </Stack>
                        {isActive && (
                          <Box
                            sx={{
                              height: 4,
                              backgroundColor: theme.palette.grey[100],
                              borderRadius: 2,
                              overflow: 'hidden'
                            }}
                          >
                            <Box
                              sx={{
                                height: '100%',
                                width: `${percentage}%`,
                                backgroundColor: '#0DAB76',
                                borderRadius: 2,
                                animation: 'fillBar 1s ease-out',
                                animationDelay: `${index * 0.1 + 0.2}s`,
                                animationFillMode: 'both',
                                '@keyframes fillBar': {
                                  '0%': {
                                    width: '0%'
                                  },
                                  '100%': {
                                    width: `${percentage}%`
                                  }
                                }
                              }}
                            />
                          </Box>
                        )}
                    </Stack>
                    );
                  })}
                </Stack>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </Paper>
    </Stack>
  );
};

export default DashboardHome;
